<script setup>
  import {computed, onBeforeMount, onBeforeUnmount, ref, watch} from "vue";

  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import additionalInformationModel from "@models/additionalInformationModel.js";
  import MedicationComponentDetailDialogAlternativeDosageData from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/Components/AlternativeDosageData.vue";
  import {checkMedicationDosageDetailDialog} from "@pages/Records/Components/Sections/MedicationBar/Utils/medicationConsistencyCheck.js";
  import {MedCheckbox, MedFormField, MedIcon, MedInfoButton, MedLabel} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {cloneDeep} from "es-toolkit";

  defineOptions({
    name: "MedicationComponentDetailDialogDosageData",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      default: false,
    },
    medicationForm: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["changeForm", "valid", "invalid"]);

  const alternativeDosageId = "alternativeDosageValidatorId";

  const refAlternativeDosage = ref(null);

  const showDosageData = ref(true);
  const discontinued = ref(false);
  const isAlternativeDosage = ref(false);
  const errors = ref([]);
  const invalidDosages = ref([]);

  const additionalInformationForm = ref(null);

  const disableDiscontinuedFields = computed(() => !discontinued.value);

  watch(
    () => discontinued.value,
    (newValue) => {
      additionalInformationForm.value.discontinued = newValue;

      if (newValue === false) {
        additionalInformationForm.value.discontinued_by = null;
        additionalInformationForm.value.discontinued_at = null;
      }

      emit("changeForm", {key: "additionalinformation", newValue: additionalInformationForm});
    },
  );

  watch(
    () => isAlternativeDosage.value,
    (newValue) => {
      if (newValue === false) {
        releaseInvalidDosages();
        emit("valid", alternativeDosageId);
        emit("valid", "checkIntervalValue");
        refAlternativeDosage.value.restoreDefaultDosage();
      } else if (refAlternativeDosage.value) {
        refAlternativeDosage.value.clearDefaultDosage();
      }
    },
  );

  onBeforeMount(() => {
    additionalInformationForm.value = cloneDeep(additionalInformationModel);

    Object.assign(additionalInformationForm.value, props.medicationForm.value.additionalinformation);

    if (
      props.medicationForm.value.dosage &&
      props.medicationForm.value.dosage.dosagealternative !== null &&
      props.medicationForm.value.dosage.dosagealternative.id !== null
    ) {
      isAlternativeDosage.value = true;
    }

    discontinued.value = props.medicationForm.value.additionalinformation?.discontinued ?? false;

    showDosageData.value = checkMedicationDosageDetailDialog(props.medicationForm.value);
  });

  onBeforeUnmount(() => {
    clearErrors();
  });

  const handleDiscontinued = (value) => {
    additionalInformationForm.value.discontinued = value;

    // set compliance to false whenever the discontinued checkbox is
    // activated because the default discontinued_by (empty selection)
    // should trigger the non-compliance check
    if (additionalInformationForm.value.discontinued_by !== "doctor") {
      additionalInformationForm.value.compliance = false;
    }

    emit("changeForm", {key: "additionalinformation", newValue: additionalInformationForm.value});
  };

  const handleDiscontinuedBy = (value) => {
    additionalInformationForm.value.discontinued_by = value;

    if (value !== "doctor" && value !== null) {
      additionalInformationForm.value.compliance = false;
    } else {
      // when discontinued_by "doctor", the compliance is set
      // to true again. note: this might cause problems if
      // other attributes of this medication have triggered
      // a non-compliance as well; those triggers would be
      // reversed when selecting discontinued by doctor afterwards)
      additionalInformationForm.value.compliance = true;
    }

    emit("changeForm", {key: "additionalinformation", newValue: additionalInformationForm.value});
  };

  const releaseInvalidDosages = () => {
    invalidDosages.value.forEach((dosageId) => {
      emit("valid", dosageId);
    });

    invalidDosages.value = [];
  };

  const checkErrors = () => {
    if (invalidDosages.value.length > 0 || errors.value.length > 0) {
      emit("invalid", alternativeDosageId);
    } else {
      emit("valid", alternativeDosageId);
    }
  };

  const handleAlternativeDosageIdsChanged = () => {
    releaseInvalidDosages();
    checkErrors();
  };

  const handleValidAlternativeDosageEvent = (dosageId) => {
    invalidDosages.value = invalidDosages.value.filter((id) => {
      return id !== dosageId;
    });

    emit("valid", dosageId);
    checkErrors();
  };

  const handleInvalidAlternativeDosageEvent = (dosageId) => {
    if (!invalidDosages.value.includes(dosageId)) {
      invalidDosages.value.push(dosageId);
    }

    emit("invalid", dosageId);
    checkErrors();
  };

  const handleAlternativeDosageTypeChange = (newType) => {
    const errorId = "alternativeDosageInvalidType";

    if (newType === null || newType === "") {
      if (!errors.value.includes(errorId)) {
        errors.value.push(errorId);
      }
    } else {
      errors.value = errors.value.filter((id) => {
        return id !== errorId;
      });
    }

    checkErrors();
  };

  const clearErrors = () => {
    releaseInvalidDosages();
    errors.value = [];
    checkErrors();
  };

  const handleDosageChange = (newDosage) => {
    emit("changeForm", {key: "dosage", newValue: newDosage});
  };
</script>

<template>
  <div>
    <button
      class="accordion-header flex w-full cursor-pointer border-gray-200 bg-white bg-[linear-gradient(0deg,#fff,#f0f0f0)] py-3 text-left text-base text-gray-800"
      :class="{'border-b': !showDosageData}"
      :data-test="getTestId('dosage-accordion-header')"
      @click.prevent="showDosageData = !showDosageData"
    >
      <div class="flex items-center font-medium">
        <MedIcon
          size="lg"
          icon="keyboard_arrow_down"
          :class="
            cn('transition-transform duration-200', {
              'rotate-180': showDosageData,
            })
          "
        />

        Dosierungsangaben
      </div>
    </button>

    <div v-show="showDosageData" class="grid grid-cols-12 gap-x-2 gap-y-4 p-2">
      <!-- alternative dosage -->
      <div class="col-span-12 col-start-1 flex h-9 items-center md:col-span-3 lg:col-span-2">
        <MedFormField orientation="horizontal">
          <MedCheckbox
            v-model="isAlternativeDosage"
            :disabled="!isEditable"
            variant="accent"
            :data-test="getTestId('dosage-alternative-checkbox')"
          />

          <MedLabel>Sonderdosierung</MedLabel>
        </MedFormField>
      </div>

      <div class="col-span-12 -mt-4 md:col-span-9 md:mt-0 lg:col-span-10">
        <MedicationComponentDetailDialogAlternativeDosageData
          v-if="isAlternativeDosage"
          ref="refAlternativeDosage"
          class="ml-6 md:ml-0"
          :medication-form="medicationForm.value"
          :is-editable="isEditable"
          @is-invalid="handleInvalidAlternativeDosageEvent"
          @is-valid="handleValidAlternativeDosageEvent"
          @alternative-dosage-ids-changed="handleAlternativeDosageIdsChanged"
          @change="handleAlternativeDosageTypeChange"
          @init-type="handleAlternativeDosageTypeChange"
          @dosage-change="handleDosageChange"
        />
      </div>

      <!-- discontinued -->
      <div class="col-span-12 col-start-1 flex h-9 items-center space-x-1 md:col-span-3 lg:col-span-2">
        <MedFormField orientation="horizontal">
          <MedCheckbox
            v-model="discontinued"
            :disabled="!isEditable"
            variant="accent"
            :data-test="getTestId('dosage-discontinued-checkbox')"
            @change="handleDiscontinued"
          />

          <MedLabel>abgesetzt</MedLabel>
        </MedFormField>

        <MedInfoButton
          size="lg"
          side="right"
          aria-label="Abgesetzt Info"
          disable-portal
          :data-test="getTestId('dosage-data-discontinued-info-button')"
        >
          <p class="mb-2 font-bold">Abgesetzt durch...</p>
          <dl class="max-w-sm space-y-2 text-sm">
            <div>
              <dt class="font-bold">„Arzt"</dt>
              <dd>
                <ul class="list-disc pl-4">
                  <li>
                    FAM wird
                    <strong>nicht</strong>
                    in Analyse berücksichtigt
                  </li>
                  <li>
                    erscheint
                    <strong>nicht</strong>
                    im BMP, da durch Arzt abgesetzt
                  </li>
                </ul>
              </dd>
            </div>
            <div>
              <dt class="font-bold">
                „Arzt; Patient weicht ab"
                <span class="font-normal">(unter Patientenangaben eintragen)</span>
              </dt>
              <dd>
                <ul class="list-disc pl-4">
                  <li>FAM wird in Analyse berücksichtigt</li>
                  <li>erscheint als „abgesetzt, Patient weicht ab" in Medikationsübersicht</li>
                  <li>
                    erscheint
                    <strong>nicht</strong>
                    im BMP, da durch Arzt abgesetzt
                  </li>
                </ul>
              </dd>
            </div>
            <div>
              <dt class="font-bold">„Patient"</dt>
              <dd>
                <ul class="list-disc pl-4">
                  <li>FAM wird in Analyse berücksichtigt</li>
                  <li>erscheint als „abgesetzt durch Patienten" in Medikationsübersicht</li>
                  <li>erscheint im BMP, da ärztlich verordnet</li>
                </ul>
              </dd>
            </div>
            <div>
              <dt class="font-bold">keine Angabe</dt>
              <dd>siehe „Patient"</dd>
            </div>
          </dl>
        </MedInfoButton>
      </div>

      <div
        v-if="discontinued"
        class="col-span-12 -mt-4 flex items-baseline space-x-2 text-sm md:col-span-9 md:mt-0 lg:col-span-10"
      >
        <div>durch</div>

        <div class="w-48">
          <ComponentSelect
            id="discontinuedBy"
            ref="requirements"
            name="discontinuedBy"
            :model-value="additionalInformationForm.discontinued_by"
            :disabled="disableDiscontinuedFields || !isEditable"
            :nullable="true"
            placeholder-text=""
            :test-id="getTestId('dosage-discontinued-by')"
            @change="handleDiscontinuedBy"
          >
            <option value="doctor">Arzt</option>
            <option value="doctor-patient-deviates">Arzt; Patient weicht ab</option>
            <option value="patient">Patient</option>
          </ComponentSelect>
        </div>

        <div>am</div>

        <div class="flex">
          <ComponentInput
            v-model="additionalInformationForm.discontinued_at"
            :disabled="!isEditable"
            type="date"
            autocomplete="off"
            :test-id="getTestId('dosage-discontinued-at')"
          />
        </div>
      </div>
    </div>
  </div>
</template>
